import { Client } from '@hey-api/client-fetch';
import { QueryClient, useQuery, UseQueryResult } from '@tanstack/solid-query';
import { Accessor } from 'solid-js';
import { deleteSplat, uploadSplat } from './upload-splat';
import {
  SplatDto,
  SplatUploadOptions,
  SplatUploadRequest,
  WaitForSplatOptions,
} from './splats-api-service.types';

interface DatasetDto {
  readonly id: string;
}

interface PageResponse<T> {
  readonly items?: readonly T[];
  readonly pageIndex?: number;
  readonly totalItems?: number;
}

const DEFAULT_PAGE_SIZE = 100;
const DEFAULT_POLL_INTERVAL_MS = 3000;
// 40 minutes: a 2 GB fileset takes about 20 to become downloadable on stage.
const DEFAULT_WAIT_TIMEOUT_MS = 2_400_000;
const PROCESSING_LIST_REFETCH_MS = 30_000;

function delay(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    signal?.throwIfAborted();
    const onAbort = (): void => {
      clearTimeout(timer);
      reject(signal?.reason);
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

function encodeTenantTrn(tenantTrn: string): string {
  return encodeURIComponent(tenantTrn);
}

export function isViewableSplat(splat: SplatDto): boolean {
  const hasTilesetUrl = splat.tilesetUrl != null && splat.tilesetUrl.length > 0;
  const hasDownloadUrl =
    splat.downloadUrl != null && splat.downloadUrl.length > 0;

  return (
    splat.isDeleted !== true &&
    splat.status === 'downloadable' &&
    (hasTilesetUrl || hasDownloadUrl)
  );
}

async function fetchAllPages<T>(
  client: Client,
  buildUrl: (pageIndex: number, pageSize: number) => string,
  signal?: AbortSignal
): Promise<T[]> {
  const results: T[] = [];
  let pageIndex = 0;
  let totalItems = Number.POSITIVE_INFINITY;

  while (results.length < totalItems) {
    const response = await client.get<PageResponse<T>>({
      url: buildUrl(pageIndex, DEFAULT_PAGE_SIZE),
      signal,
    });

    if (response.error) {
      throw new Error(`Splats API request failed: ${response.error}`);
    }

    const page = response.data;
    if (page == null) {
      break;
    }

    const items = page.items ?? [];
    totalItems = Number(page.totalItems ?? items.length);

    if (items.length === 0) {
      break;
    }

    results.push(...items);
    pageIndex += 1;

    if (items.length < DEFAULT_PAGE_SIZE || results.length >= totalItems) {
      break;
    }
  }

  return results;
}

type ListSplatsQueryOptions = {
  readonly queryKey: readonly [
    'splats',
    string | undefined,
    string | undefined,
  ];
  queryFn(): Promise<SplatDto[]>;
  readonly enabled: boolean;
  refetchInterval(query: {
    state: { data?: readonly SplatDto[] };
  }): number | false;
};

function createListSplatsQueryOptions(
  splatsApi: Accessor<SplatsApiService | undefined>,
  tenantTrn: Accessor<string | undefined>,
  projectId: Accessor<string | undefined>
): ListSplatsQueryOptions {
  return {
    queryKey: ['splats', tenantTrn(), projectId()] as const,
    queryFn: async (): Promise<SplatDto[]> => {
      const api = splatsApi();
      const trn = tenantTrn();
      if (api === undefined || trn === undefined) {
        return [];
      }

      return api.listAllSplats(trn);
    },
    enabled:
      splatsApi() !== undefined &&
      tenantTrn() !== undefined &&
      projectId() !== undefined,
    // Nothing tells the client when a splat finishes processing.
    refetchInterval: (query) =>
      query.state.data?.some((splat) => splat.status === 'processing')
        ? PROCESSING_LIST_REFETCH_MS
        : false,
  };
}

/** Registers one splat list observer for the provider lifetime. */
export function useSplatsListQuery(
  splatsApi: Accessor<SplatsApiService | undefined>,
  tenantTrn: Accessor<string | undefined>,
  projectId: Accessor<string | undefined>
): UseQueryResult<SplatDto[], Error> {
  return useQuery(() =>
    createListSplatsQueryOptions(splatsApi, tenantTrn, projectId)
  );
}

export class SplatsApiService {
  constructor(
    private readonly client: Client,
    private readonly queryClient: QueryClient
  ) {}

  public listSplatsQuery(
    tenantTrn: Accessor<string | undefined>,
    projectId: Accessor<string | undefined>
  ): UseQueryResult<SplatDto[], Error> {
    return useQuery(() =>
      createListSplatsQueryOptions(() => this, tenantTrn, projectId)
    );
  }

  public async listAllSplats(
    tenantTrn: string,
    signal?: AbortSignal
  ): Promise<SplatDto[]> {
    const encodedTenantTrn = encodeTenantTrn(tenantTrn);

    const datasets = await fetchAllPages<DatasetDto>(
      this.client,
      (pageIndex, pageSize) =>
        `/tenants/${encodedTenantTrn}/datasets?PageIndex=${pageIndex}&PageSize=${pageSize}`,
      signal
    );

    if (datasets.length === 0) {
      return [];
    }

    const splatsByDataset = await Promise.all(
      datasets.map((dataset) =>
        fetchAllPages<SplatDto>(
          this.client,
          (pageIndex, pageSize) =>
            `/tenants/${encodedTenantTrn}/datasets/${dataset.id}/splats?PageIndex=${pageIndex}&PageSize=${pageSize}`,
          signal
        )
      )
    );

    // Includes splats that are not viewable yet; see isViewableSplat.
    return splatsByDataset.flat().filter((splat) => splat.isDeleted !== true);
  }

  public async getSplat(
    tenantTrn: string,
    datasetId: string,
    splatId: string,
    signal?: AbortSignal
  ): Promise<SplatDto> {
    signal?.throwIfAborted();

    const splat = await this.queryClient.fetchQuery({
      queryKey: ['splat', tenantTrn, datasetId, splatId],
      queryFn: async ({ signal: querySignal }): Promise<SplatDto> => {
        const response = await this.client.get<SplatDto>({
          url: `/tenants/${encodeTenantTrn(tenantTrn)}/datasets/${datasetId}/splats/${splatId}`,
          signal: querySignal,
        });

        if (response.error || response.data == null) {
          throw new Error(
            `Failed to load Gaussian splat ${splatId}: ${response.error ?? 'empty response'}`
          );
        }

        return response.data;
      },
      staleTime: 0,
    });

    signal?.throwIfAborted();
    return splat;
  }

  /**
   * Creates the splat, PUTs the file to its pre-signed URL, and completes the
   * upload. See {@link uploadSplat} for progress, cancel, and cleanup.
   */
  public uploadSplat(
    tenantTrn: string,
    request: SplatUploadRequest,
    options: SplatUploadOptions = {}
  ): Promise<SplatDto> {
    return uploadSplat(this.client, tenantTrn, request, options);
  }

  public deleteSplat(
    tenantTrn: string,
    datasetId: string,
    splatId: string
  ): Promise<void> {
    return deleteSplat(this.client, tenantTrn, datasetId, splatId);
  }

  public async waitForDownloadableSplat(
    tenantTrn: string,
    datasetId: string,
    splatId: string,
    options: WaitForSplatOptions = {}
  ): Promise<SplatDto> {
    const pollIntervalMs = options.pollIntervalMs ?? DEFAULT_POLL_INTERVAL_MS;
    const deadline =
      Date.now() + (options.timeoutMs ?? DEFAULT_WAIT_TIMEOUT_MS);

    while (Date.now() < deadline) {
      await delay(pollIntervalMs, options.signal);
      const splat = await this.getSplat(
        tenantTrn,
        datasetId,
        splatId,
        options.signal
      );
      // Done only when the splat can be shown, the same test the lists use.
      if (isViewableSplat(splat)) {
        return splat;
      }
      // After a 202 from complete, the splat stays uploadable until the
      // server has assembled the upload.
      const isPending =
        splat.status === 'uploadable' ||
        splat.status === 'processing' ||
        splat.status === 'downloadable';
      if (!isPending || splat.isDeleted === true) {
        throw new Error(
          `Gaussian splat ${splatId} stopped processing with status ${splat.status}`
        );
      }
    }
    throw new Error(`Timed out waiting for Gaussian splat ${splatId}`);
  }

  public invalidateSplatsList(tenantTrn: string): Promise<void> {
    return this.queryClient.invalidateQueries({
      queryKey: ['splats', tenantTrn],
    });
  }
}
